/**
 * School ↔ published/active mapping drawer.
 * Used for **active** school mapping from plan detail and the plans list. Published school selection uses `PublishAndUpdateDrawer` (same as “Publish and update”).
 */
import { useCallback, type ReactElement } from 'react';
import { plansOfStudyRepository } from '@/repositories';
import { normalizeCheckedSchoolNidsForPublishAndUpdate } from '@/utils/plan-publish-and-update';
import { isPosReadOnly } from '@/utils/cp-context';
import { useToastNotifications } from '@/hooks/use-toast-notifications';
import { SchoolsDrawer } from './schools-drawer';
import {
  PLAN_SCHOOLS_MAPPING_COPY,
  buildActiveSchoolsSaveErrorHeading,
  buildActiveSchoolsChangeSuccessHeadings,
  buildPublishedSchoolsSaveErrorHeading,
  buildPublishedSchoolsSaveSuccessHeading,
  getSchoolsListAndSelectionForMappingMode,
  mergeActiveSchoolSelection,
  type PlanSchoolsMappingDrawerProps,
} from './plan-schools-mapping';
import { buildNoSchoolsSelectedForPublishToast } from './publish-schedule-copy';

export type { PlanSchoolsMappingDrawerProps } from './plan-schools-mapping';

/** Wired SchoolsDrawer for publish / active-schools updates (listing + plan detail). */
export function PlanSchoolsMappingDrawer({
  planId,
  planName,
  mode,
  publishedSchools,
  activeSchools,
  allSchoolsSorted,
  isSchoolsLoading = false,
  readOnly,
  onSaveStart,
  onSaveEnd,
  onClose,
}: PlanSchoolsMappingDrawerProps): ReactElement {
  const viewOnly = readOnly ?? isPosReadOnly();
  const publishMutation = plansOfStudyRepository.usePublishAndUpdatePlanOfStudy();
  const activeSchoolsMutation = plansOfStudyRepository.useUpdateActiveSchools();
  const { showToast } = useToastNotifications();

  const copy = PLAN_SCHOOLS_MAPPING_COPY[mode];
  const description = copy.description;
  const { allSchools, initialSelectedNids, lockedNids } = getSchoolsListAndSelectionForMappingMode(
    mode,
    publishedSchools,
    activeSchools,
    allSchoolsSorted
  );

  const handleSave = useCallback(
    (ids: string[]) => {
      if (viewOnly) {
        return;
      }
      void (async () => {
        try {
          if (mode === 'published') {
            const publishedNids = normalizeCheckedSchoolNidsForPublishAndUpdate(ids);
            if (publishedNids.length === 0) {
              showToast('error', buildNoSchoolsSelectedForPublishToast());
              return;
            }
            await publishMutation.mutateAsync({
              planId,
              checkedSchoolNids: publishedNids,
              mode: 'IMMEDIATE',
            });
            showToast('success', {
              heading: buildPublishedSchoolsSaveSuccessHeading(planName, publishedNids, allSchools),
            });
          } else {
            const activeSchoolNids = mergeActiveSchoolSelection(activeSchools, allSchools, ids);
            await activeSchoolsMutation.mutateAsync({ planId, activeSchoolNids });
            const headings = buildActiveSchoolsChangeSuccessHeadings(
              planName,
              Array.from(initialSelectedNids),
              activeSchoolNids,
              allSchools
            );
            headings.forEach((heading) => {
              showToast('success', { heading });
            });
          }
        } catch {
          showToast('error', {
            heading:
              mode === 'published'
                ? buildPublishedSchoolsSaveErrorHeading(planName)
                : buildActiveSchoolsSaveErrorHeading(planName),
          });
        } finally {
          onSaveEnd?.();
        }
      })();
    },
    [
      mode,
      planId,
      planName,
      allSchools,
      initialSelectedNids,
      publishMutation,
      activeSchoolsMutation,
      activeSchools,
      showToast,
      onSaveEnd,
      viewOnly,
    ]
  );

  return (
    <SchoolsDrawer
      key={`${mode}-${planId}`}
      id={`${mode}-schools-drawer`}
      mode={mode}
      title={copy.title}
      planName={planName}
      description={description}
      allSchools={allSchools}
      initialSelectedNids={initialSelectedNids}
      lockedNids={lockedNids}
      isSchoolsLoading={isSchoolsLoading}
      readOnly={readOnly}
      onBeforeSave={onSaveStart}
      onClose={onClose}
      onSave={handleSave}
    />
  );
}
